import { useMemo, useSyncExternalStore } from "react";
import { BASE_URL } from "../utils/config";
import { loadRequestQueue } from "../utils/loadRequestQueue";

const empty = { properties: [], supplies: [], loading: false, error: null };
const disabledStore = { subscribe: () => () => {}, getSnapshot: () => empty, refresh: () => {} };
const stores = new Map();

function createStore(user, key) {
  let snapshot = { ...empty, loading: true };
  let controller;
  let timer;
  let pending = false;
  let loaded = false;
  const listeners = new Set();
  const refresh = async () => {
    if (pending || document.visibilityState === "hidden") return;
    pending = true;
    const current = new AbortController();
    controller = current;
    snapshot = { ...snapshot, loading: !loaded, error: null };
    listeners.forEach(listener => listener());
    let timeout;
    const deadline = new Promise((_, reject) => {
      timeout = setTimeout(() => {
        reject(new Error("Requests are taking too long to load. Please retry."));
        current.abort();
      }, 30000);
    });
    try {
      const data = await Promise.race([loadRequestQueue(BASE_URL, user, current.signal), deadline]);
      if (controller === current) {
        loaded = true;
        snapshot = { ...data, loading: false, error: null };
      }
    } catch (error) {
      // A failed refresh must not turn a known count into zero.
      if (controller === current) snapshot = { ...snapshot, loading: false, error };
    } finally {
      clearTimeout(timeout);
      if (controller === current) {
        pending = false;
        listeners.forEach(listener => listener());
      }
    }
  };
  const store = {
    getSnapshot: () => snapshot,
    refresh,
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) {
        stores.set(key, store);
        window.addEventListener("focus", refresh);
        window.addEventListener("settings:signatories", refresh);
        document.addEventListener("visibilitychange", refresh);
        timer = setInterval(refresh, 60000);
      }
      // Opening the request page also refreshes the sidebar's shared snapshot.
      refresh();
      return () => {
        listeners.delete(listener);
        if (!listeners.size) {
          controller?.abort();
          controller = null;
          pending = false;
          clearInterval(timer);
          window.removeEventListener("focus", refresh);
          window.removeEventListener("settings:signatories", refresh);
          document.removeEventListener("visibilitychange", refresh);
          stores.delete(key);
        }
      };
    },
  };
  return store;
}

export function useRequestQueue(user, enabled = true) {
  const data = user?.data || user;
  const id = data?._id || "";
  const name = data?.username || "";
  const role = String(data?._role || data?.role || "").trim().toLowerCase();
  const store = useMemo(() => {
    if (!enabled || (!id && !name)) return disabledStore;
    const key = JSON.stringify([id, name, role]);
    if (!stores.has(key)) stores.set(key, createStore({ _id: id, username: name, role }, key));
    return stores.get(key);
  }, [id, name, role, enabled]);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
  return { ...snapshot, refresh: store.refresh };
}
